import { useState, useEffect } from 'react'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components/macro'
import { Button } from 'components'
import { missingForPayment } from 'lib/completeness'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
`

const NextSteps = styled.ol`
	margin: 4px 0 28px;
	padding: 0;
	list-style: none;
	counter-reset: s;
	li {
		counter-increment: s;
		position: relative;
		padding: 0 0 14px 44px;
		color: ${props => props.theme.mutedColor};
	}
	li::before {
		content: counter(s);
		position: absolute;
		left: 0;
		top: 0;
		width: 28px;
		height: 28px;
		border-radius: 50%;
		background: ${props => props.theme.navyColor};
		color: #fff;
		font-size: 14px;
		font-weight: 700;
		display: grid;
		place-items: center;
	}
`

const Note = styled.p`
	background: #fff3cd;
	border: 1px solid #ffc107;
	color: #664d03;
	padding: 12px 16px;
	border-radius: 6px;
	font-size: 15px;
`

const all = (...targets) => {
	for (let i = 0; i < targets.length; i++) {
		if (targets[i] !== true) {
			return false
		}
	}
	return true
}

const formatPrice = value => {
	return (value / 100).toFixed(2).replace('.', ',') + ' €'
}

const formatPercentage = value => {
	return String(value / 100).replace('.', ',') + ' %'
}

const SummaryTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	margin: 0 0 24px;
	background: ${props => props.theme.softColor};
	border-radius: ${props => props.theme.radius};
	overflow: hidden;

	td {
		padding: 12px 20px;
		color: ${props => props.theme.mutedColor};
	}

	tr.separator td {
		padding: 0 20px;
		border-bottom: 2px solid ${props => props.theme.navyColor};
	}

	tr.sum td {
		padding: 16px 20px 18px;
		font-size: 28px;
		font-weight: 700;
		color: ${props => props.theme.navyColor};
	}

	td.val {
		text-align: right;
		white-space: nowrap;
		width: 40%;
	}

	@media (max-width: 600px) {
		table-layout: fixed;
		td {
			padding: 10px 12px;
			hyphens: manual;
		}
		tr.separator td {
			padding: 0 12px;
		}
		tr.sum td {
			padding: 14px 12px 16px;
			font-size: 22px;
		}
	}
`

const ClaimsBox = styled.div`
	margin-bottom: 24px;
	label {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 6px 0;
		cursor: pointer;
	}
	input {
		width: 20px;
		height: 20px;
		margin: 4px 0 0;
		flex: 0 0 auto;
		accent-color: ${props => props.theme.navyColor};
	}
`

const Summary = ({ amount = 0, total = 0, vat = 0, vatPercentage = 0 }) => {
	return (
		<SummaryTable>
			<tbody>
				<tr>
					<td>Yrityssaneeraus&shy;hakemus</td>
					<td className="val">{formatPrice(amount)}</td>
				</tr>
				<tr>
					<td>ALV {formatPercentage(vatPercentage)}</td>
					<td className="val">{formatPrice(vat)}</td>
				</tr>
				<tr className="separator">
					<td></td>
					<td></td>
				</tr>
				<tr className="sum">
					<td>Yhteensä</td>
					<td className="val">{formatPrice(total)}</td>
				</tr>
			</tbody>
		</SummaryTable>
	)
}

const Claims = ({ value, onChange }) => {
	const initValue = { terms: false, privacy: false }
	const [v, setV] = useState(initValue)

	const handleChange = e => {
		setV(val => {
			val[e.target.name] = e.target.checked
			return { ...val }
		})
	}

	useEffect(() => {
		if (all(v.terms, v.privacy)) {
			onChange(true)
			return
		}
		onChange(false)
	}, [v, onChange])

	useEffect(() => {
		if (value === true) {
			setV({ terms: true, privacy: true })
			return
		}
		setV(initValue)
	}, [value])

	return (
		<ClaimsBox>
			<label>
				<input type="checkbox" name="terms" onChange={handleChange} />
				<span>
					Hyväksyn palvelun{' '}
					<a
					href="https://yrityskorjaamo.fi/yleiset-palveluehdot"
					target="_blank"
					rel="noopener noreferrer"
				>
					käyttöehdot
					</a>
				</span>
			</label>
			<label>
				<input type="checkbox" name="privacy" onChange={handleChange} />
				<span>
					Olen lukenut palvelun{' '}
					<a
					href="https://yrityskorjaamo.fi/tietosuojaseloste"
					target="_blank"
					rel="noopener noreferrer"
				>
					tietosuojaselosteen
					</a>
				</span>
			</label>
		</ClaimsBox>
	)
}

const Payment = ({ application, onMoveTo }) => {
	const history = useHistory()
	const [data, setData] = useState({})
	const [status, setStatus] = useState('loading')
	const [error, setError] = useState(null)
	const [claims, setClaims] = useState(false)
	const [demoMode, setDemoMode] = useState(false)
	const [simulatePayments, setSimulatePayments] = useState(false)
	const [paying, setPaying] = useState(false)

	useEffect(() => {
		axios
			.get('/api/site-config')
			.then(res => {
				setDemoMode(!!res.data.demoMode)
				setSimulatePayments(!!res.data.simulatePayments)
			})
			.catch(() => {})
	}, [])

	const fetchPayment = () => {
		axios
			.get('/api/payments/' + application.id)
			.then(res => {
				setData(res.data)
				setStatus('success')
				setError(null)
			})
			.catch(err => {
				setStatus('error')
				setError(err)
			})
	}

	const missing = missingForPayment(application)
	const loaded = !!application.id
	const complete = loaded && missing.length === 0

	useEffect(() => {
		if (complete) {
			fetchPayment()
		}
		// eslint-disable-next-line
	}, [application.id, complete])

	if (loaded && !complete) {
		return (
			<Wrapper>
				<h1>Hakemuksesta puuttuu tietoja</h1>
				<p className="lead">
					Täytä seuraavat kohdat ennen maksamista:
				</p>
				<NextSteps>
					{missing.map(m => (
						<li key={m.page}>{m.label}</li>
					))}
				</NextSteps>
				<Button
					variant="filled"
					style={{ width: '100%' }}
					onClick={() => onMoveTo && onMoveTo(missing[0].page)}
				>
					Siirry täyttämään: {missing[0].label}
				</Button>
			</Wrapper>
		)
	}

	const handleDemoPay = async () => {
		setPaying(true)
		setError(null)
		try {
			await axios.post(`/api/applications/${application.id}/demo-pay`)
			history.push(`/form/${application.id}/signing`)
		} catch (err) {
			setError('Maksun käsittely epäonnistui')
			setPaying(false)
		}
	}

	if (status === 'loading') {
		return 'Ladataan...'
	}

	if (status === 'error') {
		return (
			<p>
				Maksun luonti epäonnistui. Tarkista, että kaikki vaiheet on täytetty, ja
				yritä uudelleen.
			</p>
		)
	}

	return (
		<Wrapper>
			<h1>Kaikki tarvittavat tiedot on nyt täytetty</h1>
			<p className="lead">Maksun jälkeen hakemus etenee näin:</p>
			<NextSteps>
				<li>
					Yrityssaneeraushakemus ja osakkeenomistajien päätöspöytäkirja
					lähetetään asianosaisille allekirjoitettaviksi.
				</li>
				<li>
					Kun kaikki ovat allekirjoittaneet, valmis hakemus lähetetään yrityksen
					kotipaikan mukaan määräytyvään käräjäoikeuteen.
				</li>
				<li>Voit tarkastella ja ladata asiakirjat heti maksun jälkeen.</li>
			</NextSteps>
			{demoMode && simulatePayments && (
				<Note>
					Demoversiossa maksu simuloidaan. Allekirjoitus tapahtuu Visma Sign
					-testiympäristössä – allekirjoittajat saavat kutsun sähköpostiinsa.
				</Note>
			)}
			{demoMode && !simulatePayments && (
				<Note>
					Demoversio: maksu tapahtuu Paytrailin kautta (testihinta). Hakemusta ei
					lähetetä käräjäoikeudelle. Allekirjoituskutsut lähtevät Visma Signista.
				</Note>
			)}
			<Summary
				amount={data.amount}
				total={data.total}
				vat={data.vat}
				vatPercentage={data.vatPercentage}
			/>
			<Claims value={claims} onChange={v => setClaims(v)} />
			{error && <p style={{ color: 'crimson' }}>{error}</p>}
			{!simulatePayments && (
				<p style={{ fontSize: 15, color: '#45535d' }}>
					Maksu välitetään Paytrailin kautta.
				</p>
			)}
			{simulatePayments ? (
				<Button
					variant="filled"
					style={{ width: '100%' }}
					disabled={!claims || paying}
					onClick={handleDemoPay}
				>
					{paying ? 'Käsitellään...' : 'Jatka maksuun ja allekirjoitukseen'}
				</Button>
			) : (
				<Button
					as="a"
					href={data.paymentLink}
					variant="filled"
					style={{ width: '100%' }}
					disabled={!claims}
				>
					Siirry maksamaan
				</Button>
			)}
		</Wrapper>
	)
}

export default Payment
